import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { integrityStyles } from "./integrityTheme";
import { CONTACT_EMAIL } from "./offers";
import { setMeta, setJsonLd, SITE_URL, breadcrumbJsonLd } from "./seo";
import SiteFooter from "./SiteFooter";

const FOUNDER = "Shaleen Kacker";
const FOUNDER_TITLE = "Software Architect and Founder, Documore";

const expertise = [
  {
    h: "AI and agentic systems",
    items: [
      "LLM integration with OpenAI and Anthropic models",
      "Agentic and multi-agent workflow orchestration",
      "Retrieval-augmented generation and evaluation",
      "OCR and document intelligence",
    ],
  },
  {
    h: "Architecture and integration",
    items: [
      "API-first and serverless architecture on AWS",
      "Data pipelines, validation and reconciliation",
      "Integrating CRMs, ERPs and third-party data providers",
      "Python, React, React Native",
    ],
  },
  {
    h: "Enterprise background",
    items: [
      "20 years of software development",
      "Senior enterprise roles in information services",
      "Earlier work in banking and insurance organizations",
      "Systems where records that disagree become losses and audit findings",
    ],
  },
];

const work = [
  {
    h: "AERO agentic framework",
    p: "A multi-agent orchestration framework for multi-step AI workflows, coordinating specialized agents with structured hand-offs.",
  },
  {
    h: "Data reliability layer",
    p: "A validation and reliability layer for high-throughput e-commerce data pipelines, built to catch failures before they reach downstream systems.",
  },
  {
    h: "Document intelligence pilots",
    p: "Pipelines that turn BOMs, client specifications, lease documents and insurance submissions into verified, structured data, with exceptions flagged instead of guessed.",
  },
  {
    h: "AI agent evaluation",
    p: "Evaluation tooling that scores chatbot and voice-agent conversations for accuracy, policy consistency, escalation and cost.",
  },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const url = `${SITE_URL}/about`;
    setMeta({
      title: `About — ${FOUNDER}, Founder of Documore`,
      description:
        "Documore is led by Shaleen Kacker, a software architect with 20 years of experience across banking, insurance and information services, who designs and builds AI and data systems end to end.",
      url,
    });
    const cleanups = [
      setJsonLd("person", {
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": `${SITE_URL}/about#founder`,
        name: FOUNDER,
        jobTitle: "Software Architect",
        worksFor: { "@id": `${SITE_URL}/#organization` },
        founder: { "@id": `${SITE_URL}/#organization` },
        url,
        sameAs: ["https://shaleenkacker.com"],
        knowsAbout: [
          "Software architecture", "AI systems", "Agentic AI", "Retrieval-augmented generation",
          "Document intelligence", "Data integrity", "System integration",
        ],
      }),
      setJsonLd("breadcrumb", breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ])),
    ];
    return () => cleanups.forEach((c) => c());
  }, []);

  return (
    <>
      <style>{integrityStyles}</style>
      <div className="di-root">
        <div className="di-wrap">
          <div className="di-hero">
            <div>
              <div className="di-kicker">
                <span className="di-kicker-line" />
                <span className="di-kicker-text">About Documore</span>
              </div>
              <h1 className="di-h1">{FOUNDER}</h1>
              <p className="di-text" style={{ marginTop: 12, fontSize: 18 }}>{FOUNDER_TITLE}</p>
            </div>
            <div style={{ display: "grid", gap: 16 }}>
              <p className="di-lede">
                I've spent 20 years building software systems in banking, insurance and information services, where
                records that disagree turn into disputes, losses and audit findings.
              </p>
              <p className="di-lede">
                Documore exists to check that agreement before a system acts: that the data, the documents and now the
                AI systems a business depends on actually line up. I design these systems and build them end to end,
                from architecture to production.
              </p>
            </div>
          </div>

          <section className="di-section" style={{ paddingTop: 16 }}>
            <hr className="di-rule" />
            <h2 className="di-h2" style={{ margin: "40px 0 24px" }}>Expertise</h2>
            <div className="di-grid-3">
              {expertise.map((e) => (
                <div key={e.h} className="di-cell">
                  <h3 className="di-h3" style={{ marginBottom: 16 }}>{e.h}</h3>
                  <ul className="di-list">{e.items.map((t) => <li key={t}>{t}</li>)}</ul>
                </div>
              ))}
            </div>
          </section>

          <section className="di-section" style={{ paddingTop: 0 }}>
            <h2 className="di-h2" style={{ marginBottom: 24 }}>Selected work</h2>
            <div className="di-grid-2">
              {work.map((w) => (
                <div key={w.h} className="di-cell">
                  <h3 className="di-h3" style={{ marginBottom: 10 }}>{w.h}</h3>
                  <p className="di-text">{w.p}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="di-section" style={{ paddingTop: 0 }}>
            <h2 className="di-h2" style={{ marginBottom: 20 }}>How we work</h2>
            <dl className="di-facts">
              <dt>Architecture and execution</dt>
              <dd>The person who designs the system builds it. No hand-offs between strategy and delivery.</dd>
              <dt>Defined standards</dt>
              <dd>What we verify, what you receive and the standard results must meet are agreed in writing before work starts.</dd>
              <dt>Straight answers</dt>
              <dd>Clear about scope, timelines and limits, including when something can't be guaranteed.</dd>
              <dt>Your data</dt>
              <dd>
                Used only for your engagement and deleted within 30 days of acceptance. <Link to="/privacy">Privacy</Link>
              </dd>
            </dl>
          </section>

          <section className="di-section" style={{ paddingTop: 0 }}>
            <hr className="di-rule" />
            <div style={{ padding: "40px 0 0", display: "grid", gap: 16, maxWidth: 760 }}>
              <h2 className="di-h2">Work with Documore</h2>
              <p className="di-text">
                See where Documore applies, or tell us about the system you're working on.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Link className="di-btn" to="/services">Explore capabilities</Link>
                <a className="di-btn-ghost" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </div>
            </div>
          </section>
        </div>
        <SiteFooter />
      </div>
    </>
  );
}
